<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div 
      class="svgWrapper" 
      v-html="svgContent" 
    />
  </div>
</template>

<script lang="ts">
import { $cabin, $selectedCabin } from '@/scss/expinet'
import { IDetailDeck } from '@/store/details/cabin/interfaces'
import { Cabin } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import 'isomorphic-fetch'
import { PropType, defineComponent } from 'vue'

export default defineComponent({
  props: {
    deck: {
      type: Object as PropType<IDetailDeck>,
      default: null,
    },
    cabins: {
      type: Array<Cabin>,
      default: [],
    },
    selectedCabinId: {
      type: String,
      default: null,
    },
  },

  data: () => {
    return {
      svgContent: '',
      styleElement: null as HTMLStyleElement | null,
    }
  },

  watch: {
    deck: {
      handler(newDeck: IDetailDeck) {
        this.computeSVGContent(newDeck.imageUrl)
        this.updateStyle()
      },
      deep: true,
    },
    selectedCabinId() {
      this.updateStyle()
    },
  },
  mounted() {
    this.computeSVGContent(this.deck.imageUrl)
    this.updateStyle()
  },
  methods: {
    getAttribute: (tag: string, attribute: string) => {
      const attr = tag.indexOf(attribute)
      const startQuote = tag.indexOf('"', attr)
      const endQuote = tag.indexOf('"', startQuote + 1)
      const value = tag.substring(startQuote + 1, endQuote)
      return value
    },

    getViewVox(tag: string) {
      const value = this.getAttribute(tag, 'viewBox')
      const numbers = value.split(' ')
      const width = parseInt(numbers[2], 10)
      const height = parseInt(numbers[3], 10)
      return { width, height }
    },

    fetchImage: async (url: string) => {
      const options: RequestInit = {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      }
      const request = new Request(url)
      const response = await fetch(request, options)

      return await response.text()
    },

    async computeSVGContent(imageUrl: string) {
      const svgText = await this.fetchImage(imageUrl)

      const svgOpen = svgText.indexOf('<svg')
      const svgClose = svgText.indexOf('>', svgOpen)
      const svgEnd = svgText.indexOf('</svg>')
      const svgTag = svgText.substring(svgOpen, svgClose)
      const svgChildren = svgText.substring(svgClose + 1, svgEnd)

      const viewBox = this.getViewVox(svgTag)
      const width = viewBox.width
      const height = viewBox.height

      const scale = 200 / width
      const w = Math.floor(scale * width)
      const h = Math.floor(scale * height)

      const attributes = {
        version: '1.1',
        xmlns: 'http://www.w3.org/2000/svg',
        'xmlns:xlink': 'http://www.w3.org/1999/xlink',
        x: '0px',
        y: '0px',
        'xml:space': 'preserve',
        style: this.getAttribute(svgTag, 'style'),
        width: `${w}px`,
        height: `${h}px`,
        viewBox: this.getAttribute(svgTag, 'viewBox'),
      }

      const xmlAttr: string[] = []
      if (!isNaN(width) && !isNaN(height)) {
        for (const attr in attributes) {
          if (!!attr) {
            xmlAttr.push(`${attr}="${attributes[attr as keyof typeof attributes]}"`)
          }
        }
      }

      const svg = `<svg id="shipViewSvg" ${xmlAttr.join(' ')}>${svgChildren}</svg>`
      this.svgContent = svg
    },

    updateStyle() {
      if (this.cabins.length === 0) {
        return
      }
      const cabins = this.cabins

      let style = this.styleElement
      if (!this.styleElement) {
        style = document.createElement('style')
        // WebKit hack :(
        style.appendChild(document.createTextNode(''))
        this.$el.appendChild(style)
        this.styleElement = style
      }

      const cssTargets = cabins.map(cabin => {
        if (cabin.id === this.selectedCabinId) {
          return ` svg#shipViewSvg g#k${cabin.id} * { fill: ${$selectedCabin()} } `
        }
        return ` svg#shipViewSvg g#k${cabin.id} * { fill: ${$cabin()}  } `
      })
      style!.innerHTML = cssTargets.join(' ')
    },
  },
})
</script>
